import React, { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, ClipboardList, BookOpen, Presentation, Camera, GraduationCap, FlaskConical, Settings, LogOut, Building2, Menu } from "lucide-react";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/daily-log", label: "Daily Tracker", icon: ClipboardList, testid: "nav-daily-log" },
  { to: "/education", label: "Education Material", icon: BookOpen, testid: "nav-education" },
  { to: "/ppt", label: "PPT / Education", icon: Presentation, testid: "nav-ppt" },
  { to: "/shoots", label: "Educational Shoots", icon: Camera, testid: "nav-shoots" },
  { to: "/training", label: "Training", icon: GraduationCap, testid: "nav-training" },
  { to: "/rd", label: "R&D", icon: FlaskConical, testid: "nav-rd" },
  { to: "/master", label: "Master", icon: Settings, testid: "nav-master", adminOnly: true },
];

export default function Layout() {
  const { user, doctor, logout } = useAuth();
  const [openMobile, setOpenMobile] = useState(false);

  const filteredNav = nav.filter(n => {
    if (n.adminOnly && !["founder","manager"].includes(user?.role)) return false;
    return true;
  });

  const roleLabel = { founder: "Founder / Super Admin", manager: "Clinical Manager", doctor: "Doctor" }[user?.role] || "";

  return (
    <div className="min-h-screen w-full flex bg-navy-900 text-foreground">
      <aside className={`${openMobile ? "block" : "hidden"} md:block fixed md:static z-40 h-screen w-64 shrink-0 bg-navy-800 border-r border-navy-700`}>
        <div className="h-16 px-5 flex items-center gap-3 border-b border-navy-700">
          <div className="h-9 w-9 rounded-md gold-bg grid place-items-center font-heading text-base font-semibold">Y</div>
          <div>
            <div className="font-heading text-base tracking-tight leading-none">Youngness</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Centre CRM</div>
          </div>
        </div>
        <nav className="p-3 space-y-1">
          {filteredNav.map(n => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid} onClick={() => setOpenMobile(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${isActive ? "bg-navy-700 text-foreground gold-border" : "text-muted-foreground hover:bg-navy-700/60 hover:text-foreground"}`}>
              <n.icon className="h-4 w-4" /><span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 border-t border-navy-700 p-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-navy-700 grid place-items-center gold-text font-semibold">{user?.name?.[0] || "?"}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground truncate">{roleLabel}</div>
            </div>
            <button data-testid="logout-btn" onClick={logout} className="text-muted-foreground hover:text-rose_alert transition-colors duration-200"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 border-b border-navy-700 bg-navy-800/60 backdrop-blur px-6 flex items-center justify-between">
          <button className="md:hidden text-muted-foreground" onClick={() => setOpenMobile(v => !v)}><Menu className="h-5 w-5" /></button>
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Building2 className="h-4 w-4 gold-text" /><span>{doctor?.branch || "All Branches"}</span></div>
          <div className="flex items-center gap-3">
            <div className="text-right"><div className="text-xs text-muted-foreground">Signed in</div><div className="text-sm">{user?.email}</div></div>
          </div>
        </header>
        <div className="flex-1 p-6 overflow-y-auto"><Outlet /></div>
      </main>
    </div>
  );
}
